import { sanitiseUrlForHost } from '@parcel2go/npm-p2g-app';
import { useCallback } from 'react';
import { NavigateOptions, useLocation, useNavigate, useParams } from 'react-router-dom';
import { CONSUMER_BASE_PATH, useNavExperience } from '~/components/nav/nav-experience';
import { isConsumerRoute } from '~/routes/groups/consumer-route-segments';

const locale = 'locale';
const prefix = 'prefix';

export interface GoToOptions {
  // Skip the consumer-experience base segment and target the absolute (root/SmartSend)
  // URL. Rarely needed - the safety net below already routes non-consumer targets
  // absolutely. Defaults to false.
  absolute?: boolean;
  navigateOptions?: NavigateOptions;
}

const useGoto = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const params = useParams();
  const experience = useNavExperience();

  const goToUrl = useCallback(
    (link: string, opts?: GoToOptions) => {
      // In the consumer experience, only prepend the `account/` base for routes that exist
      // in the consumer subtree. Anything else (a SmartSend-only target) resolves to the
      // absolute URL so cross-experience links land on the real page instead of a dead
      // `account/...` path. The business (default) experience never prepends.
      const useConsumerBase = experience === 'consumer' && !opts?.absolute && isConsumerRoute(link);
      const path = `${useConsumerBase ? `${CONSUMER_BASE_PATH}/` : ''}${link}`;
      return params[locale] ? `/${params[locale]}/${params[prefix]}/${path}` : `/${params[prefix]}/${path}`;
    },
    [params, experience]
  );

  const goToUrlFullyQualified = useCallback(
    (link: string, opts?: GoToOptions) => sanitiseUrlForHost(goToUrl(link, opts), location.host, true),
    [goToUrl]
  );

  const goTo = useCallback(
    (link: string, opts?: GoToOptions) => {
      navigate(goToUrl(link, opts), opts?.navigateOptions);
    },
    [navigate, goToUrl]
  );

  // Stays on the current URL, just replaces the history entry's state.
  const replaceLocationState = useCallback(
    (state: unknown) => {
      navigate(routerLocation.pathname + routerLocation.search, { replace: true, state });
    },
    [navigate, routerLocation.pathname, routerLocation.search]
  );

  return { goTo, goToUrl, goToUrlFullyQualified, replaceLocationState } as const;
};

export default useGoto;
